import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { addMainTab, updateSubTabs } from "src/modules/auth/tabsSlice";
import { axiosInstance } from "src/api/config/axiosInstance";

interface CreateTabProps {
  patientId?: number | null;
  patientName?: string | null;
  title: string;
  element: React.ReactNode;
  path?: string;
}

interface ComponentInfo {
  component: React.ReactNode;
  path: string;
  title: string;
  patientId?: number | null;
  elementType?: string;
}

const componentRegistry = new Map<string, ComponentInfo>();

const componentFactory = new Map<string, () => React.ReactNode>();
const wrapperComponentFactory = new Map<string, () => React.ReactNode>();
const componentCache = new Map<string, React.ReactNode>();

export const registerComponent = (path: string, componentFactoryFn: () => React.ReactNode) => {
  componentFactory.set(path, componentFactoryFn);
};

export const registerWrapperComponent = (path: string, componentFactoryFn: () => React.ReactNode) => {
  wrapperComponentFactory.set(path, componentFactoryFn);
};



export const createComponentFromPath = (path: string, patientId?: number | null): React.ReactNode | null => {
  const requiresPatientId = path && (
    path.includes('/patient/') ||
    path.includes('patientId') ||
    path.includes('ptId')
  );

  if (requiresPatientId && (patientId === undefined || patientId === null)) {
    console.log('⚠️ PatientId gerekli ama undefined/null:', path);
    return null;
  }

  const cacheKey = `${path}-${patientId || 'null'}`;

  if (componentCache.has(cacheKey)) {
    return componentCache.get(cacheKey);
  }

  const enhanceComponent = (comp: React.ReactNode) => {
    if (patientId && React.isValidElement(comp)) {
      return React.cloneElement(comp as React.ReactElement, {
        patientId,
        ptId: patientId.toString()
      } as any);
    }
    return comp;
  };

  let factoryFn = componentFactory.get(path);
  if (factoryFn) {
    try {
      const component = factoryFn();
      const enhanced = enhanceComponent(component);
      componentCache.set(cacheKey, enhanced);
      return enhanced;
    } catch (error) {
      console.log('⚠️ Component oluşturma hatası:', path, error);
    }
  }

  factoryFn = wrapperComponentFactory.get(path);
  if (factoryFn) {
    try {
      const component = factoryFn();
      const enhanced = enhanceComponent(component);
      componentCache.set(cacheKey, enhanced);
      return enhanced;
    } catch (error) {
      console.log('Wrapper component oluşturma hatası:', path, error);
    }
  }
  const basePath = path.split('?')[0];

  if (basePath !== path) {
    factoryFn = componentFactory.get(basePath);
    if (factoryFn) {
      try {
        const component = factoryFn();
        const enhanced = enhanceComponent(component);
        componentCache.set(cacheKey, enhanced);
        return enhanced;
      } catch (error) {
        console.log('Component oluşturma hatası:', basePath, error);
      }
    }

    factoryFn = wrapperComponentFactory.get(basePath);
    if (factoryFn) {
      try {
        const component = factoryFn();
        const enhanced = enhanceComponent(component);
        componentCache.set(cacheKey, enhanced);
        return enhanced;
      } catch (error) {
        console.log('Wrapper component oluşturma hatası:', basePath, error);
      }
    }
  }

  const pathSegments = basePath.split('/');
  if (pathSegments.length >= 2) {
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (/^\d+$/.test(lastSegment)) {
      const parentPath = pathSegments.slice(0, -1).join('/');
      
      factoryFn = wrapperComponentFactory.get(parentPath);
      if (factoryFn) {
        try {
          const component = factoryFn();
          const enhanced = enhanceComponent(component);
          componentCache.set(cacheKey, enhanced);
          return enhanced;
        } catch (error) {
          console.log('Wrapper component oluşturma hatası (parent path):', parentPath, error);
        }
      }

      factoryFn = componentFactory.get(parentPath);
      if (factoryFn) {
        try {
          const component = factoryFn();
          const enhanced = enhanceComponent(component);
          componentCache.set(cacheKey, enhanced);
          return enhanced;
        } catch (error) {
          console.log('Component oluşturma hatası (parent path):', parentPath, error);
        }
      }
    }
  }

  const registryEntries = Array.from(componentRegistry.entries());
  for (const [componentId, info] of registryEntries) {
    if (info.path === path && info.patientId === patientId) {
      componentCache.set(cacheKey, info.component);
      return info.component;
    }
  }

  return null;
};

const saveComponentToRegistry = (componentId: string, component: React.ReactNode, path: string, title: string, patientId?: number | null, elementType?: string) => {
  componentRegistry.set(componentId, {
    component,
    path,
    title,
    patientId,
    elementType
  });
};

const clearComponentFromRegistry = (componentId: string) => {
  componentRegistry.delete(componentId);
};

export const useTabComponent = (componentId: string | null) => {
  const tabs = useSelector((state: any) => state.tabs.mainTabs);

  if (!componentId) return null;
  const componentInfo = componentRegistry.get(componentId);

  if (!componentInfo?.component) {
    let tabInfo = null;
    for (const mainTab of tabs) {
      if (mainTab.componentId === componentId) {
        tabInfo = { path: mainTab.path, patientId: mainTab.data?.patientId };
        break;
      }
      if (mainTab.subTabs) {
        for (const subTab of mainTab.subTabs) {
          if (subTab.componentId === componentId) {
            tabInfo = { path: subTab.path, patientId: mainTab.data?.patientId };
            break;
          }
        }
        if (tabInfo) break;
      }
    }

    if (tabInfo) {
      const newComponent = createComponentFromPath(tabInfo.path, tabInfo.patientId);
      if (newComponent) {
        saveComponentToRegistry(componentId, newComponent, tabInfo.path, '', tabInfo.patientId);
        return newComponent;
      }
    }
  }

  return componentInfo?.component || null;
};

const resolveTabComponent = (componentId: string, tabs: any[]): React.ReactNode | null => {
  const componentInfo = componentRegistry.get(componentId);

  if (!componentInfo?.component) {
    let tabInfo: { path: string; patientId?: number | null } | null = null;

    for (const mainTab of tabs) {
      if (mainTab.componentId === componentId) {
        tabInfo = { path: mainTab.path, patientId: mainTab.data?.patientId };
        break;
      }
      if (mainTab.subTabs) {
        for (const subTab of mainTab.subTabs) {
          if (subTab.componentId === componentId) {
            tabInfo = { path: subTab.path, patientId: mainTab.data?.patientId };
            break;
          }
        }
        if (tabInfo) break;
      }
    }

    if (tabInfo) {
      const newComponent = createComponentFromPath(tabInfo.path, tabInfo.patientId);
      if (newComponent) {
        saveComponentToRegistry(componentId, newComponent, tabInfo.path, "", tabInfo.patientId);
        return newComponent;
      }
    }
  }

  return componentInfo?.component || null;
};

export const useTabComponents = (componentIds: Array<string | null>) => {
  const tabs = useSelector((state: any) => state.tabs.mainTabs);

  return React.useMemo(() => {
    const components: Record<string, React.ReactNode> = {};

    for (const componentId of componentIds) {
      if (!componentId) continue;
      const resolved = resolveTabComponent(componentId, tabs);
      if (resolved !== null) {
        components[componentId] = resolved;
      }
    }

    return components;
  }, [componentIds, tabs]);
};

export const getComponentInfo = (componentId: string | null) => {
  if (!componentId) return null;
  return componentRegistry.get(componentId) || null;
};

export const clearTabComponent = (componentId: string) => {
  clearComponentFromRegistry(componentId);
};

export const clearComponentCache = () => {
  componentCache.clear();
};

export const registerComponentOnDemand = (path: string, componentFactoryFn: () => React.ReactNode) => {
  if (componentFactory.has(path)) {
    return;
  }
  componentFactory.set(path, componentFactoryFn);
};

const patientNameCache = new Map<number, string>();

const getPatientName = async (patientId: number | null): Promise<string> => {
  if (!patientId) return `Hasta`;

  // Check cache first
  if (patientNameCache.has(patientId)) {
    return patientNameCache.get(patientId)!;
  }

  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_API_URL}/nurse/api/Patient/GetPatientInfo/${patientId}`
    );

    if (response.status === 200 && response.data?.data) {
      const { name, surname } = response.data.data;
      const fullName = `${name || ''} ${surname || ''}`.trim() || `Hasta ${patientId}`;

      // Cache the result
      patientNameCache.set(patientId, fullName);
      return fullName;
    }
  } catch (error) {
    console.error("Error fetching patient name:", error);
  }

  return `Hasta ${patientId}`;
};

export interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
  parentId?: string;
  type?: 'master' | 'sub';
  data?: any;
  path?: string;
  onClose?: () => void;
}

export default function CreateTab({ patientId = null, patientName = null, title, element, path }: CreateTabProps) {
  const dispatch = useDispatch();
  const tabs = useSelector((state: any) => state.tabs.mainTabs);
  const shouldUpdateUrl = useSelector((state: any) => state.tabs.shouldUpdateUrl);
  const location = useLocation();
  //const [mountKey] = useState(() => Math.random().toString(36));

  const updateUrl = (newPath: string) => {
    if (!shouldUpdateUrl || !window.history?.pushState || !newPath) return;

    // Normal URL'ler oluştur (hash olmadan)
    let fullUrl = newPath.startsWith('/') ? newPath : `/${newPath}`;

    // Path temizleme işlemleri
    fullUrl = fullUrl
      .replace(/\/+/g, '/')
      .replace(/^\/+|\/+$/g, '');

    const segments = fullUrl.split('/');
    const uniqueSegments = segments.filter((segment, index) => segments.indexOf(segment) === index);
    fullUrl = '/' + uniqueSegments.join('/');

    if (fullUrl.includes('/nurse/medcard')) {
      fullUrl = fullUrl.replace(/\/nurse\/medcard\/+/g, '/nurse/medcard/');
    }
    fullUrl = fullUrl.replace(/\/([^/]+\/[^/]+)(?:\/\1)+/g, '/$1');

    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentFullUrl = currentPath + currentSearch;

    // URL'yi güncelle - subtab'lar için önemli
    if (currentFullUrl !== fullUrl) {
      window.history.pushState({}, '', fullUrl);
    }
  };

  useEffect(() => {
    const tabId = path ? path.replace(/\//g, '-') : title.toLowerCase().replace(/\s+/g, '-');

    // Normal query parametrelerini al
    const currentSearch = window.location.search;
    const searchParams = new URLSearchParams(currentSearch);
    searchParams.delete('cardName');
    searchParams.delete('orId');
    const cleanSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const fullPath = path?.includes('?') ? path : (path + cleanSearch);

    if (patientId) {
      // Hastalı sekmeler için olan mantık
      const masterTabId = `patient-${patientId.toString()}`;
      const subTabId = `${masterTabId}-${tabId}`;
      const existingMasterTab = tabs.find((tab: any) => tab.id === masterTabId);

      if (existingMasterTab) {
        const existingSubTab = existingMasterTab.subTabs.find((tab: any) => tab.id === subTabId);
        if (!existingSubTab) {
          // Yeni alt tab oluştur
          saveComponentToRegistry(subTabId, element, fullPath, title, patientId);
          const newSubTab = {
            id: subTabId,
            label: title,
            componentId: subTabId,
            path: fullPath, // Query parametreleriyle birlikte path'i sakla
            data: { patientId }
          };
          dispatch(updateSubTabs({ id: masterTabId, subTabs: [...existingMasterTab.subTabs, newSubTab] }));
          // Subtab için URL'yi güncelle - query parametreleriyle birlikte
          updateUrl(fullPath);
        } else {
          // Alt tab zaten varsa, sadece component'i yeniden kaydet
          saveComponentToRegistry(subTabId, element, fullPath, title, patientId);
          // URL'yi güncelleme, sadece component'i yeniden kaydet
        }
      } else {
        // Yeni ana tab oluştur
        const createPatientTab = async () => {
          // Önce prop'tan gelen ismi kontrol et, yoksa API'den al
          const finalPatientName = patientName || await getPatientName(patientId);
          const newSubTabId = `${masterTabId}-${tabId}`;
          const currentUrlParams = new URLSearchParams(window.location.search);
          const orIdRef = currentUrlParams.get('orId');

          saveComponentToRegistry(newSubTabId, element, fullPath, title, patientId);
          dispatch(addMainTab({
            id: masterTabId,
            label: finalPatientName,
            componentId: null,
            path: `/patient/${patientId}`,
            subTabs: [{
              id: newSubTabId,
              label: title,
              componentId: newSubTabId,
              path: fullPath, // Query parametreleriyle birlikte path'i sakla
              data: { patientId, orId: orIdRef }
            }],
            activeSubTab: 0,
            data: { patientId, patientName: finalPatientName, orId: orIdRef }
          }));
          // Yeni ana tab için URL'yi güncelle
          updateUrl(fullPath);
        };
        createPatientTab();
      }
    } else {
      // Normal sekmeler için ana mantık
      const existingTab = tabs.find((tab: any) => tab.id === tabId);

      if (!existingTab) {
        // Sekme Redux state'inde yoksa yeniden oluştur.
        saveComponentToRegistry(tabId, element, fullPath, title, patientId);
        dispatch(addMainTab({
          id: tabId,
          label: title,
          componentId: tabId,
          path: fullPath // Query parametreleriyle birlikte path'i sakla
        }));
        // Sadece yeni tab oluşturulduğunda URL'yi güncelle
        updateUrl(fullPath);
      } else {
        // Tab zaten varsa, sadece component'i yeniden kaydet
        saveComponentToRegistry(tabId, element, fullPath, title, patientId);
        // URL'yi güncelleme, sadece component'i yeniden kaydet
      }
    }
  }, [patientId, title, element, path, location]);

  return null;
}
